'use client'

import dynamic from 'next/dynamic'
import classNames from 'classnames'
import { useResponsive, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import ErrorBoundary from '@dustin-web/microsite-components/src/utils/error-boundary'
import type { ProductPageType } from '@productsite-beta/src/components/product-page'
import { useOptimisticImage } from '../product-context/product-optimistic-context'
import ProductGallery from './components/product-gallary/product-gallery'
import ProductIndentifiers from './components/product-identifiers/product-identifiers'

import './product-information.scss'

const EnergyLabel = dynamic(() => import('@dustin-web/product-components/src/energy-label'))
const ProductUtilityBar = dynamic(
  () => import('./components/product-utility-bar/product-utility-bar')
)
const ProductDescription = dynamic(
  () => import('./components/product-description/product-description')
)
const ProductSpecifications = dynamic(
  () => import('./components/product-specifications/product-specifications')
)

type Props = {
  product: ProductPageType
  isMobile: boolean
  pathname: string
}

const ProductInfomration = ({ product, isMobile, pathname }: Props) => {
  const { isSmallScreen } = useResponsive()
  const { translate } = useTranslations()
  const { optimisticImageId } = useOptimisticImage()
  const {
    id,
    nameSlug,
    manufacturerName,
    displayName,
    pageDisplayData,
    displaySpecifications,
    energyClass,
    energyDocumentId,
    energyLabelImageId,
    manufacturerProductIdentifier,
    promotedSpecifications,
  } = product ?? {}

  const isSmall = isSmallScreen || isMobile
  const serverImages = product?.pageDisplayData?.imageIds ?? []

  const productImages = optimisticImageId
    ? [optimisticImageId, ...serverImages.slice(1)]
    : serverImages

  return (
    <section className={classNames('product-information', { mobile: isSmall })}>
      <div className="gallery-wrapper">
        {energyClass && (
          <ErrorBoundary>
            <EnergyLabel
              energyClass={energyClass}
              energyDocumentId={energyDocumentId}
              energyLabelImageId={energyLabelImageId}
              productId={id}
              nameSlug={nameSlug}
              productTitle={`${manufacturerName} ${displayName} ${displaySpecifications}`}
              width={isSmall ? 60 : 80}
              labelDirection={isSmall ? 'left' : 'right'}
              textAlignment={isSmall ? 'bottom' : 'top'}
              togglePopoverOnHover
              className="product-energy-label"
            />
          </ErrorBoundary>
        )}
        <ErrorBoundary>
          <ProductGallery
            productImages={productImages}
            productName={displayName}
            isSmallScreen={isSmall}
          />
        </ErrorBoundary>
      </div>
      {product.availability?.isAvailableForSale && (
        <ErrorBoundary>
          <ProductUtilityBar isSmallScreen={isSmall} product={product} />
        </ErrorBoundary>
      )}
      <div className="overview-wrapper">
        <ErrorBoundary>
          <ProductIndentifiers
            productId={id}
            productIdLabel={translate('ProductID')}
            manufacturerName={manufacturerName}
            manufacturerProductIdentifier={manufacturerProductIdentifier!}
          />
        </ErrorBoundary>
        <div className="contents">
          {promotedSpecifications?.length > 0 && (
            <ErrorBoundary>
              <ProductSpecifications
                title={translate('SectionHeadingProductShortSpecification')}
                pathname={pathname}
                linkText={translate('view_all_specifications')}
                specifications={promotedSpecifications}
              />
            </ErrorBoundary>
          )}
          {pageDisplayData && pageDisplayData.keySellingPoints?.length > 0 && (
            <ErrorBoundary>
              <ProductDescription
                title={translate('SectionHeadingProducKeySellingPoints')}
                linkText={translate('view_all_description')}
                pathname={pathname}
                description={pageDisplayData.keySellingPoints!}
              />
            </ErrorBoundary>
          )}
        </div>
      </div>
    </section>
  )
}

export default ProductInfomration
